import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

const SoftwareTestimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  
  const testimonials = [
    {
      id: 1,
      name: 'Sarah Johnson',
      position: 'Owner',
      company: 'Small Retail Business',
      image: '👩‍💼',
      quote: 'I was skeptical about AI at first - I\'m not a tech person at all! But their team explained everything in plain English and showed me how it could help my store. The website they built for us now automatically helps customers find exactly what they need, and my staff saves hours on inventory management.',
      stats: {
        leads: 'Customer Service',
        conversion: 'Improved 87%',
        revenue: '$28K Saved'
      }
    },
    {
      id: 2,
      name: 'Michael Chen',
      position: 'Founder',
      company: 'Marketing Agency',
      image: '👨‍💼',
      quote: 'Our $199 consultation call saved us from wasting thousands on the wrong AI tools. They built us a simple system that writes first drafts of our client proposals and email campaigns. What used to take my team days now takes hours. No complicated jargon, just real results for our business.',
      stats: {
        leads: 'Proposal Creation',
        conversion: '5x Faster',
        revenue: '$42K Added'
      }
    },
    {
      id: 3,
      name: 'Jessica Martinez',
      position: 'Director',
      company: 'Non-Profit Organization',
      image: '👩‍💻',
      quote: 'With limited budget and resources, we needed to work smarter. Their team helped us implement simple automation that sorts through donation data and volunteer applications. Now we can focus on our mission instead of paperwork. The whole experience was refreshingly straightforward.',
      stats: {
        leads: 'Admin Hours',
        conversion: '-75%',
        revenue: '$35K Saved'
      }
    }
  ];
  
  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };
  
  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };
  
  const goToTestimonial = (index) => {
    setActiveTestimonial(index);
  };
  
  const activeTestimonialData = testimonials[activeTestimonial];
  
  return (
    <TestimonialsContainer id="testimonials">
      <SectionHeader>
        <TestimonialBadge>CLIENT SUCCESS</TestimonialBadge>
        <SectionTitle>Real Results From <HighlightText>REAL BUSINESSES</HighlightText></SectionTitle>
        <SectionDescription>
          Hear from everyday business owners who have transformed their operations with our <strong>custom AI solutions</strong>.
        </SectionDescription>
      </SectionHeader>
      
      <TestimonialCard>
        <QuoteIconContainer>
          <QuoteIcon>"</QuoteIcon>
        </QuoteIconContainer>
        <TestimonialContent>
          <TestimonialQuote>
            {activeTestimonialData.quote}
          </TestimonialQuote>
          
          <ResultsContainer>
            <ResultBox>
              <ResultLabel>{activeTestimonialData.stats.leads}</ResultLabel>
              <ResultValue>{activeTestimonialData.stats.conversion}</ResultValue>
            </ResultBox>
            <ResultBox>
              <ResultLabel>Development</ResultLabel>
              <ResultValue>US-Based</ResultValue>
            </ResultBox>
            <ResultBox>
              <ResultLabel>Business Impact</ResultLabel>
              <ResultValue>{activeTestimonialData.stats.revenue}</ResultValue>
            </ResultBox>
          </ResultsContainer>
          
          <TestimonialAuthor>
            <ProfileIcon>{activeTestimonialData.image}</ProfileIcon>
            <AuthorInfo>
              <AuthorName>{activeTestimonialData.name}</AuthorName>
              <AuthorTitle>{activeTestimonialData.position}, {activeTestimonialData.company}</AuthorTitle>
            </AuthorInfo>
          </TestimonialAuthor>
        </TestimonialContent>
        
        <TestimonialControls>
          <ArrowButton onClick={prevTestimonial} direction="left">
            ←
          </ArrowButton>
          <DotsContainer>
            {testimonials.map((_, index) => (
              <Dot 
                key={index} 
                isActive={index === activeTestimonial}
                onClick={() => goToTestimonial(index)}
              />
            ))}
          </DotsContainer>
          <ArrowButton onClick={nextTestimonial} direction="right">
            →
          </ArrowButton>
        </TestimonialControls>
      </TestimonialCard>
      
      <CTAContainer>
        <CTAText>Ready to see similar results in your business?</CTAText>
        <CTAButton href="#contact">Schedule Your Consultation</CTAButton>
      </CTAContainer>
    </TestimonialsContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const quote = keyframes`
  0% {
    transform: translateY(10px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const TestimonialsContainer = styled.section`
  padding: 100px 80px;
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 991px) {
    padding: 80px 40px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const TestimonialBadge = styled.div`
  display: inline-block;
  background-color: #e9f7fe;
  color: #024A5F;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 0.9rem;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HighlightText = styled.span`
  color: #FF4757;
  display: block;
  margin-top: 10px;
`;

const SectionDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  line-height: 1.6;
  
  strong {
    color: #024A5F;
  }
`;

const TestimonialCard = styled.div`
  position: relative;
  background: white;
  border-radius: 20px;
  padding: 60px 50px 40px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
  margin: 0 auto 60px;
  max-width: 1000px;
  animation: ${fadeIn} 1s ease-out;
  border: 1px solid #eaeaea;
  
  @media (max-width: 768px) {
    padding: 50px 30px 30px;
  }
`;

const QuoteIconContainer = styled.div`
  position: absolute;
  top: -30px;
  left: 50px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #024A5F, #0871A4);
  color: white;
  border-radius: 50%;
  box-shadow: 0 10px 25px rgba(2, 74, 95, 0.25);
  
  @media (max-width: 768px) {
    top: -25px;
    left: 30px;
    width: 50px;
    height: 50px;
  }
`;

const QuoteIcon = styled.div`
  font-size: 3rem;
  font-family: Georgia, serif;
  font-weight: bold;
  line-height: 1;
  margin-top: -10px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const TestimonialContent = styled.div`
  animation: ${quote} 0.5s ease-out;
`;

const TestimonialQuote = styled.p`
  font-size: 1.3rem;
  line-height: 1.8;
  color: #444;
  margin-bottom: 30px;
  font-style: italic;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.7;
  }
`;

const ResultsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const ResultBox = styled.div`
  background: linear-gradient(135deg, #f8f9fa, #e9f7fe);
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid #eaeaea;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
    border-color: #024A5F;
  }
`;

const ResultLabel = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 5px;
  font-weight: 600;
`;

const ResultValue = styled.h4`
  font-size: 1.8rem;
  font-weight: 800;
  color: #024A5F;
  margin: 0;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  margin-top: 30px;
`;

const ProfileIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1a95c9, #39b9ef);
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  box-shadow: 0 5px 15px rgba(26, 149, 201, 0.2);
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
    margin-right: 12px;
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 5px;
`;

const AuthorTitle = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0;
`;

const TestimonialControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 40px;
`;

const ArrowButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 1px solid #eaeaea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: #024A5F;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #024A5F;
    color: white;
    transform: translateX(${({ direction }) => direction === 'left' ? '-5px' : '5px'});
  }
  
  &:focus {
    outline: none;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0 20px;
`;

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ isActive }) => isActive ? '#024A5F' : '#e0e0e0'};
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ isActive }) => isActive ? '#024A5F' : '#bdbdbd'};
  }
`;

const CTAContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
`;

const CTAText = styled.p`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  background: linear-gradient(90deg, #FF4757, #FF6B81);
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
  padding: 18px 40px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 10px 25px rgba(255, 71, 87, 0.3);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(255, 71, 87, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 15px 30px;
    font-size: 1rem;
  }
`;

export default SoftwareTestimonials; 